import { API_BASE_URL } from '@synergy/config';
import {
    BonusSettlementCustomerStatus,
    BonusSettlementSupplierStatus,
    DocumentStatus,
    getBonusSettlementCustomerTranslationId,
    getBonusSettlementSupplierTranslationId,
} from '@synergy/models';
import { Box, Stack } from '@mui/material';
import { Filter, LegacyDateRangeFilter } from '@synergy/components';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import {
    PaloAmount,
    PaloColumn,
    PaloFilterModal,
    PaloListView,
    PaloSegmentedToggle,
    PaloStatusPill,
} from '@synergy/components-palo';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { getDocumentStatusTranslationId } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { Api } from '../../../api';
import { LegacySupplierFilter } from '../filter/legacy-supplier-filter';
import { ROUTES } from '../../../features/sidebar-menu/navigation-drawer/routes';
import { ResponsibleContactFilter } from '../filter/responsible-contact-filter';

/**
 * The five things a member has going with us (#2193). A price info is not a
 * document of its own but a PDF sent from an inquiry, and only members receive
 * one, so a supplier's page lists the other four.
 */
export type PaloTransactionKind = 'inquiry' | 'priceInfo' | 'purchaseOrder' | 'invoice' | 'bonusSettlement';

const KINDS_BY_SIDE: Record<'customer' | 'supplier', PaloTransactionKind[]> = {
    customer: ['inquiry', 'priceInfo', 'purchaseOrder', 'invoice', 'bonusSettlement'],
    supplier: ['inquiry', 'purchaseOrder', 'invoice', 'bonusSettlement'],
};

const kindLabelId: Record<PaloTransactionKind, string> = {
    inquiry: 'app.navigation.inquiries',
    priceInfo: 'app.transactions.kind.priceInfos',
    purchaseOrder: 'app.navigation.purchaseOrders',
    invoice: 'app.navigation.invoices',
    bonusSettlement: 'app.navigation.bonusSettlements',
};

/** A single document of each kind, as its row names it. */
const kindRowLabelId: Record<PaloTransactionKind, string> = {
    inquiry: 'app.document.inquiry',
    priceInfo: 'app.transactions.kind.priceInfo',
    purchaseOrder: 'app.document.purchaseOrder',
    invoice: 'app.document.invoice',
    bonusSettlement: 'app.transactions.kind.bonusSettlement',
};

const kindColor: Record<PaloTransactionKind, { ink: string; soft: string }> = {
    inquiry: { ink: tokens.color.kindInquiry, soft: tokens.color.kindInquirySoft },
    priceInfo: { ink: tokens.color.kindPriceInfo, soft: tokens.color.kindPriceInfoSoft },
    purchaseOrder: { ink: tokens.color.kindOrder, soft: tokens.color.kindOrderSoft },
    invoice: { ink: tokens.color.kindInvoice, soft: tokens.color.kindInvoiceSoft },
    bonusSettlement: { ink: tokens.color.kindBonus, soft: tokens.color.kindBonusSoft },
};

const documentStatus = (status: DocumentStatus): ReactNode => (
    <PaloStatusPill status={status} label={<FormattedMessage id={getDocumentStatusTranslationId(status)} />} />
);

/**
 * The kind of a document as a tag. Squarer than a status pill and without its
 * icon, so the two do not read as the same thing side by side.
 */
const KindTag = ({ kind }: { kind: PaloTransactionKind }): ReactElement => (
    <Box
        component="span"
        sx={{
            display: 'inline-block',
            padding: '1px 7px',
            borderRadius: '4px',
            fontSize: '11.5px',
            fontWeight: 560,
            whiteSpace: 'nowrap',
            color: kindColor[kind].ink,
            backgroundColor: kindColor[kind].soft,
        }}
    >
        <FormattedMessage id={kindRowLabelId[kind]} />
    </Box>
);

/** One line of the list, whatever kind of document it came from. */
interface PaloTransaction {
    kind: PaloTransactionKind;
    id: number;
    number: string;
    date?: Date;
    /** The other side of the document — a supplier on a member's page, and the reverse. */
    counterparty?: string;
    employee?: string;
    /** Net, for every kind: the order list carries no gross total. */
    amount?: number;
    currency?: string;
    /** Each kind has statuses of its own; a price info has none. */
    status?: ReactNode;
    route: string;
}

export interface PaloAccountTransactionsProps {
    accountId: number;
    /** Which side of a document this account stands on. */
    side: 'customer' | 'supplier';
}

const nameOf = (account?: { name?: string }): string | undefined => account?.name;

const contactName = (contact?: { fullName?: string }): string | undefined => contact?.fullName || undefined;

/** Names of the other side, without the gaps where one is missing. */
const joined = (names?: (string | undefined)[]): string | undefined => {
    const present = (names ?? []).filter(Boolean);

    return present.length > 0 ? present.join(', ') : undefined;
};

/**
 * A consolidated view of what runs between us and one account, so the question
 * "what do we have with them?" is answered on their page instead of in three
 * lists with three filters.
 *
 * It reads the document lists that already exist, each filtered to this
 * account — nothing is stored twice.
 */
export const PaloAccountTransactions = ({ accountId, side }: PaloAccountTransactionsProps): ReactElement => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [kind, setKind] = useState<PaloTransactionKind | 'all'>('all');
    const [rows, setRows] = useState<PaloTransaction[]>([]);
    const kinds = KINDS_BY_SIDE[side];
    const [totals, setTotals] = useState<Record<PaloTransactionKind, number>>({
        inquiry: 0,
        priceInfo: 0,
        purchaseOrder: 0,
        invoice: 0,
        bonusSettlement: 0,
    });
    const [requesting, setRequesting] = useState(false);

    // The filter bar writes its values into the address bar as filter[...],
    // which is exactly what the three list endpoints read — so the period, the
    // supplier and the employee narrow the stock itself, not just the page
    // that was already fetched.
    const carried = searchParams.toString();

    const params = useCallback((): URLSearchParams => {
        const search = new URLSearchParams({ limit: '50', page: '1', sort: 'createdAt', order: 'desc' });

        new URLSearchParams(carried).forEach((value, key) => {
            if (key.startsWith('filter[')) {
                search.append(key, value);
            }
        });

        search.set('customer' === side ? 'filter[customerAccountId]' : 'filter[supplierAccountId]', String(accountId));

        return search;
    }, [accountId, side, carried]);

    useEffect(() => {
        let current = true;
        const api = new Api(API_BASE_URL);

        const load = async (): Promise<void> => {
            setRequesting(true);

            const [inquiries, orders, invoices, priceInfos, bonusSettlements] = await Promise.all([
                api.inquiries.cget(params()),
                api.purchaseOrders.cget(params()),
                api.invoices.cget(params()),
                'customer' === side ? api.accounts.getPriceInfos(accountId, params()) : Promise.resolve(null),
                api.accounts.getBonusSettlements(accountId, side, params()),
            ]);

            if (!current) {
                return;
            }

            const collected: PaloTransaction[] = [];
            const counted: Record<PaloTransactionKind, number> = {
                inquiry: 0,
                priceInfo: 0,
                purchaseOrder: 0,
                invoice: 0,
                bonusSettlement: 0,
            };

            if (inquiries.success) {
                counted.inquiry = inquiries.result.total;
                inquiries.result.items.forEach(inquiry =>
                    collected.push({
                        kind: 'inquiry',
                        id: inquiry.id,
                        number: inquiry.number,
                        date: inquiry.createdAt,
                        // An inquiry asks several suppliers at once, so both
                        // sides are listed rather than picked from.
                        counterparty:
                            'customer' === side
                                ? joined(inquiry.supplierNames)
                                : joined(inquiry.accountPurchases?.map(entry => nameOf(entry.customerAccount))),
                        employee: contactName(inquiry.responsibleContact),
                        status: documentStatus(inquiry.status),
                        route: ROUTES.inquiriesDetail.replace(':id', String(inquiry.id)),
                    })
                );
            }

            if (orders.success) {
                counted.purchaseOrder = orders.result.total;
                orders.result.items.forEach(order =>
                    collected.push({
                        kind: 'purchaseOrder',
                        id: order.id,
                        number: order.number,
                        date: order.orderDate ?? order.createdAt,
                        counterparty:
                            'customer' === side ? nameOf(order.supplierAccount) : nameOf(order.customerAccount),
                        employee: contactName(order.responsibleContact),
                        // The order list only carries the net total. A cancelled
                        // order counts against the total, as in the order list.
                        amount:
                            DocumentStatus.Canceled === order.status
                                ? -Math.abs(order.totalNetPrice)
                                : order.totalNetPrice,
                        currency: order.currency,
                        status: documentStatus(order.status),
                        route: ROUTES.purchaseOrdersDetail.replace(':id', String(order.id)),
                    })
                );
            }

            if (invoices.success) {
                counted.invoice = invoices.result.total;
                invoices.result.items.forEach(invoice =>
                    collected.push({
                        kind: 'invoice',
                        id: invoice.id,
                        number: invoice.number,
                        date: invoice.orderDate ?? invoice.createdAt,
                        counterparty:
                            'customer' === side ? nameOf(invoice.supplierAccount) : nameOf(invoice.recipientAccount),
                        employee: contactName(invoice.responsibleContact),
                        amount: invoice.totalNetPrice,
                        currency: invoice.currency,
                        status: documentStatus(invoice.status),
                        route: ROUTES.invoices.detail.replace(':id', String(invoice.id)),
                    })
                );
            }

            if (priceInfos?.success) {
                counted.priceInfo = priceInfos.result.total;
                priceInfos.result.items.forEach(priceInfo =>
                    collected.push({
                        kind: 'priceInfo',
                        id: priceInfo.mediaId,
                        // Named by the inquiry it was sent from, which is also
                        // where a click leads.
                        number: priceInfo.inquiryNumber,
                        date: new Date(priceInfo.sentAt),
                        counterparty: priceInfo.inquiryTitle ?? undefined,
                        employee: priceInfo.responsibleContactName ?? undefined,
                        route: ROUTES.inquiriesDetail.replace(':id', String(priceInfo.inquiryId)),
                    })
                );
            }

            if (bonusSettlements.success) {
                counted.bonusSettlement = bonusSettlements.result.total;
                bonusSettlements.result.items.forEach(settlement =>
                    collected.push({
                        kind: 'bonusSettlement',
                        id: settlement.supplierBonusSettlementId ?? settlement.bonusSettlementId,
                        number: String(settlement.year),
                        date: new Date(settlement.date),
                        // A member without a bonus payout receives nothing, and
                        // an amount there would suggest otherwise.
                        amount:
                            null === settlement.amount || false === settlement.hasBonusPayout
                                ? undefined
                                : settlement.amount,
                        status:
                            'customer' === side ? (
                                <PaloStatusPill
                                    status={settlement.status as BonusSettlementCustomerStatus}
                                    label={
                                        <FormattedMessage
                                            id={getBonusSettlementCustomerTranslationId(
                                                settlement.status as BonusSettlementCustomerStatus
                                            )}
                                        />
                                    }
                                />
                            ) : (
                                <PaloStatusPill
                                    status={settlement.status as BonusSettlementSupplierStatus}
                                    label={
                                        <FormattedMessage
                                            id={getBonusSettlementSupplierTranslationId(
                                                settlement.status as BonusSettlementSupplierStatus
                                            )}
                                        />
                                    }
                                />
                            ),
                        route:
                            'customer' === side
                                ? ROUTES.bonusSettlements.customerDetail
                                      .replace(':bonusSettlementId', String(settlement.bonusSettlementId))
                                      .replace(':id', String(accountId))
                                : ROUTES.bonusSettlements.supplierDetail
                                      .replace(':bonusSettlementId', String(settlement.bonusSettlementId))
                                      .replace(':id', String(settlement.supplierBonusSettlementId)),
                    })
                );
            }

            setRows(collected);
            setTotals(counted);
            setRequesting(false);
        };

        void load();

        return () => {
            current = false;
        };
    }, [params, side, accountId]);

    const shown = useMemo(() => {
        // Sort on the instant, not on the rendering: these are Date objects,
        // and comparing them as text throws.
        const at = (value?: Date): number => (value ? new Date(value).getTime() : 0);

        return ('all' === kind ? rows : rows.filter(row => row.kind === kind))
            .slice()
            .sort((left, right) => at(right.date) - at(left.date));
    }, [rows, kind]);

    const columns: PaloColumn<PaloTransaction>[] = [
        {
            key: 'date',
            type: 'date',
            head: <FormattedMessage id="app.table.column.date" />,
            cell: row =>
                row.date ? <FormattedDate value={row.date} day="2-digit" month="2-digit" year="numeric" /> : null,
        },
        {
            key: 'kind',
            type: 'classification',
            head: <FormattedMessage id="app.table.column.type" />,
            cell: row => <KindTag kind={row.kind} />,
        },
        {
            key: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            // A draft has no number yet; its id is no number anyone can look up (#2316).
            cell: row => row.number ?? <FormattedMessage id="app.convertReceipt.draft" />,
        },
        {
            key: 'counterparty',
            type: 'name',
            head: <FormattedMessage id="app.table.column.counterparty" />,
            cell: row => row.counterparty,
        },
        {
            key: 'employee',
            type: 'name',
            head: <FormattedMessage id="app.common.responsibleContact" />,
            cell: row => row.employee,
        },
        {
            key: 'amount',
            currency: 'EUR',
            type: 'amount',
            head: <FormattedMessage id="app.table.column.netAmount" />,
            cell: row =>
                undefined === row.amount ? null : <PaloAmount value={row.amount} currency={row.currency} base="EUR" />,
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: row => row.status ?? null,
        },
    ];

    const filters = (
        <PaloFilterModal>
            <LegacyDateRangeFilter />
            <ResponsibleContactFilter />
            {/* On a member's page the counterparty is the supplier; on a
                supplier's page that filter is the account itself. */}
            {'customer' === side && <LegacySupplierFilter />}
        </PaloFilterModal>
    );

    return (
        <Filter
            searchParams={searchParams}
            onUpdateSearchParams={next => setSearchParams(next, { replace: true })}
            useLegacyFilter={true}
        >
            <PaloListView<PaloTransaction, string>
                embedded={true}
                filters={filters}
                headerActions={
                    <PaloSegmentedToggle
                        value={kind}
                        onChange={value => setKind(value as PaloTransactionKind | 'all')}
                        options={[
                            { value: 'all', label: <FormattedMessage id="app.common.all" /> },
                            ...kinds.map(entry => ({
                                value: entry,
                                label: <FormattedMessage id={kindLabelId[entry]} />,
                            })),
                        ]}
                    />
                }
                /* A line of figures, not a wall of cards: the counts are read in
                   passing. */
                title={
                    <Stack direction="row" gap={2} sx={{ fontSize: 12, color: tokens.color.ink2 }}>
                        {kinds.map(entry => (
                            <Box key={entry} component="span">
                                <FormattedMessage id={kindLabelId[entry]} />
                                {': '}
                                <Box
                                    component="span"
                                    sx={{ color: tokens.color.ink, fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {totals[entry]}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                }
                columns={columns}
                items={shown}
                getRowId={row => `${row.kind}-${row.id}`}
                requesting={requesting}
                emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                onRowClick={row => navigate(row.route)}
            />
        </Filter>
    );
};
